// frontend/src/features/tasks/TaskHistory.tsx
import { useState } from 'react';
import { Stack, Text, Paper, Divider, Group, Loader, Timeline, Badge, Anchor } from '@mantine/core';
import { IconCheck, IconCalendar } from '@tabler/icons-react';
import type { TaskCompletion } from '@/types/task';

interface TaskHistoryProps {
  createdAt?: string;
  completions: TaskCompletion[];
  isLoading: boolean;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function TaskHistory({ createdAt, completions, isLoading }: TaskHistoryProps) {
  const defaultLimit = 3;
  const [showAll, setShowAll] = useState(false);
  const displayedCompletions = showAll ? completions : completions.slice(0, defaultLimit);

  return (
    <Paper p="md" withBorder mt="md" bg="gray.0">
      <Stack gap="sm">
        <Group justify="space-between">
          <Text size="sm" fw={600} c="dimmed">
            Task History
          </Text>
          <Badge variant="light" size="sm">
            {completions.length} completions
          </Badge>
        </Group>

        <Divider />

        {createdAt && (
          <Group gap="xs">
            <IconCalendar size={14} color="var(--mantine-color-dimmed)" />
            <Text size="sm" c="dimmed">
              Created {formatDate(createdAt)}
            </Text>
          </Group>
        )}

        {isLoading ? (
          <Group justify="center" p="md">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">Loading history...</Text>
          </Group>
        ) : completions.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            No completions yet
          </Text>
        ) : (
          <Timeline active={displayedCompletions.length} bulletSize={20} lineWidth={2}>
            {displayedCompletions.map((completion) => (
              <Timeline.Item
                key={completion.id}
                bullet={<IconCheck size={12} />}
                title={formatDateTime(completion.completed_at)}
              >
                {completion.notes && (
                  <Text size="xs" c="dimmed" mt={4}>
                    {completion.notes}
                  </Text>
                )}
              </Timeline.Item>
            ))}
          </Timeline>
        )}

        {completions.length > defaultLimit && (
          <Group justify="center" gap="xs">
            <Anchor
              component="button"
              type="button"
              size="xs"
              onClick={() => setShowAll((prev) => !prev)}
            >
              {showAll ? 'Show less' : `Show all ${completions.length} completions`}
            </Anchor>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}
